import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useRouter } from 'next/router';
import PubSub from 'pubsub-js';
import { useApolloClient, useQuery } from '@apollo/client';
import hive from '@bombas/hive';
import { isBrowser } from '@bombas/react/util';
import { convertVariantIdToGlobalId } from '@bombas/shopify';
import { SITE_CONFIG } from '../../../App.gql';
import { isPreview } from '../../../../graphql/previews';

import {
  getCart,
  addToCart,
  updateItemQuantity,
  applyDiscountCode,
  setCustomAttributes
} from '../../../../services/storefront/cart';
import {
  sanitizeCart,
  validateDiscountCodes,
  compileCartItems,
  trackDiscountApplication
} from '../../Cart.utils';
import CartDialog from './CartDialog';
import { QBS_ENABLED } from '../../../../util/const';

const CartDialogContainer = ({ config, ...props }) => {
  const cartDialogRef = useRef(null);
  const [isOpen, setIsOpen] = useState(false);
  const [contents, setContents] = useState(null);
  const client = useApolloClient();
  const router = useRouter();

  const newUserCoupon = config?.coupon_code?.value;
  const loyaltyCoupon = config?.loyalty_coupon?.value;
  const codeDiscountPercentage = config?.coupon_code_percentage?.value;
  const { discount } = router.query;
  const allowableCouponCodes = [newUserCoupon, loyaltyCoupon, discount].filter(
    Boolean
  );

  const isDiscountAutoApplied = config?.is_discount_auto_applied?.value;

  const isCartPage = isBrowser() && window.location.pathname === '/cart';

  const handleQuantityUpdate = (...args) =>
    updateItemQuantity(...args, client)
      .then((c) => setContents(c))
      // TODO: Phase 3 handle caught error in toast.
      .catch((e) => e);

  const handleAddToCart = ({
    variantId,
    quantity,
    pimData,
    customAttributes,
    productId,
    source
  }) =>
    addToCart(
      variantId,
      quantity,
      pimData,
      customAttributes,
      client,
      productId,
      source
    )
      .then((c) => {
        setContents(c);

        // Only open cart dialog if NOT on the cart page
        if (!isCartPage) {
          setIsOpen(true);

          const convertedCurrentVariantId =
            convertVariantIdToGlobalId(variantId);

          // Transform the cart lines to an array of objects with globalId and quantity
          const cartVariantIdAndQuantity = c.lines.edges.map(({ node }) => ({
            globalId: node.merchandise.id,
            quantity: node.quantity
          }));

          const isNewItem = cartVariantIdAndQuantity.some(
            (item) =>
              item.globalId === convertedCurrentVariantId && item.quantity < 2
          );

          // If the product is previously not in the cart, scroll to the top of the CartDialog upon adding to cart
          if (cartDialogRef.current && isNewItem) {
            cartDialogRef.current.scrollTop = 0;
          }
        } else {
          // On cart page, scroll to top
          PubSub.publish('cart.scrollToTop');
        }
      })
      // TODO: Phase 3 handle caught error in toast.
      .catch((e) => e);
  useEffect(() => {
    const addToken = PubSub.subscribe(
      'cart.add',
      (
        _,
        {
          variantId,
          quantity,
          pimData,
          customAttributes = undefined,
          autoClose = true,
          productId = null,
          source = 'Unknown'
        }
      ) => {
        handleAddToCart(
          {
            variantId,
            quantity,
            pimData,
            customAttributes,
            productId,
            source
          },
          autoClose
        );
      }
    );

    const openToken = PubSub.subscribe('cart.open', async () => {
      // Prevent CartDialog from opening on Cart page

      if (!isCartPage) setIsOpen(true);
      hive.track('CART_VIEW');
    });

    const closeToken = PubSub.subscribe('cart.close', () => {
      setIsOpen(false);
    });

    const discountApplyToken = PubSub.subscribe(
      'cart.discountApply',
      (_, couponCode) => {
        applyDiscountCode([couponCode], client)
          .then((c) => {
            setContents(c);
          })
          // TODO: Phase 3 handle caught error in toast.
          .catch((e) => e);
      }
    );

    const refreshCartContents = () =>
      getCart(client)
        .then(sanitizeCart)
        .then(validateDiscountCodes(allowableCouponCodes, client))
        .then(trackDiscountApplication)
        .then((cart) => {
          if (isDiscountAutoApplied) {
            applyDiscountCode([newUserCoupon], client)
              .then((c) => {
                setContents(c);
              })
              // TODO: Phase 3 handle caught error in toast.
              .catch((e) => e);
          } else if (discount) {
            applyDiscountCode([discount], client)
              .then((c) => {
                setContents(c);
              })
              // TODO: Phase 3 handle caught error in toast.
              .catch((e) => e);
          } else {
            setContents(cart);
          }
        })
        // TODO: Phase 3 handle caught error in toast.
        .catch((e) => e);

    // Preload cart contents
    refreshCartContents();

    const refreshToken = PubSub.subscribe('cart.update', refreshCartContents);

    return () => {
      PubSub.unsubscribe(openToken);
      PubSub.unsubscribe(addToken);
      PubSub.unsubscribe(discountApplyToken);
      PubSub.unsubscribe(refreshToken);
      PubSub.unsubscribe(closeToken);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      hive.track('MINICART_OPEN');
    }
  }, [isOpen]);

  // Sync cart contents to data layer
  useEffect(() => {
    if (!contents) return;

    /**
     * fetches the value of the _disableDiscount custom attribute set on cart add
     * if it is defined, then disableDiscount has been set by CHECKOUT_CREATE, otherwise the user already has an existing cart
     */
    const disableDiscount = contents?.attributes?.find(
      (attr) => attr.key === '_disableDiscount'
    )?.value;

    /**
     * in the case of an old checkout object (checkout_create query not called):
     * if the user has an existing cart but does not have the QBS A/B Test cookie, we want to add this new customAttribute in
     * if the user has an existing cart and DOES have the cookie, we want to update the customAttribute to its correct value
     * this will only run ONCE per user as disableDiscount will be set a value if it is undefined regardless if a user has the QBS A/B Test cookie
     */

    if (disableDiscount === undefined) {
      const updatedAttrs = contents?.attributes || [];

      /**
       * we check for undefined here because we want to ensure that all carts have the _disableDiscount attribute
       * confirmed with BE that default value of _disableDiscount is 'false' on their end
       */
      if (QBS_ENABLED) {
        updatedAttrs.push({
          key: '_disableDiscount',
          value: 'false'
        });
      } else {
        updatedAttrs.push({
          key: '_disableDiscount',
          value: 'true'
        });
      }

      setCustomAttributes(updatedAttrs, client);
    }

    /**
     * new cart created by CHECKOUT_CREATE:
     * this solves the issue of being bucketed in test for the single tier ab test (disableDiscount will be false), but in control for the multi tier ab test
     * this means that disableDiscount should be true
     * also:
     * we want to update the value of disableDiscount if the user adds an item from quickAdd in PCP and then navigates to a PDP and gets bucketed into the CONTROL variation
     * this condition will only run ONCE per user because disableDiscount will be set to 'true' and the variation will still be 'control'
     */
    if (disableDiscount === 'false' && !QBS_ENABLED) {
      /** we can access contents.attributes without optional chaining because disableDiscount having a value means contents.attributes exist */
      const updatedAttrs = contents.attributes || [];

      /** Checkout obj creation via QuickAdd sets _disableDiscount to false, therefore we need to find the index of the customAttribute and update it to true if users are bucketed into the control variation of the A/B test */
      const disableDiscountIndex = updatedAttrs.findIndex(
        (attr) => attr.key === '_disableDiscount'
      );

      updatedAttrs[disableDiscountIndex].value = 'true';

      setCustomAttributes(updatedAttrs, client);
    }

    /**
     * switching over from single tier to multi tier test -
     * this solves the issue of being bucketed in control for the single tier ab test (disableDiscount will be true), but in test for the multi tier ab test
     * this means that disableDiscount should be false
     */
    if (disableDiscount === 'true' && QBS_ENABLED) {
      /** we can access contents.attributes without optional chaining because disableDiscount having a value means contents.attributes exist */
      const updatedAttrs = contents.attributes || [];

      /** Checkout obj creation via QuickAdd sets _disableDiscount to false, therefore we need to find the index of the attribute and update it to true if users are bucketed into the control variation of the A/B test */
      const disableDiscountIndex = updatedAttrs.findIndex(
        (attr) => attr.key === '_disableDiscount'
      );

      updatedAttrs[disableDiscountIndex].value = 'false';

      setCustomAttributes(updatedAttrs, client);
    }

    const items = compileCartItems(contents?.lines?.edges);

    // Track cart sync event with ecommerce data
    hive.track('CART_SYNC', {
      ecommerce: {
        currency: contents?.subtotalAmount?.currencyCode,
        value: contents?.subtotalAmount?.amount,
        items
      }
    });
  }, [contents]);

  return (
    <CartDialog
      {...props}
      ref={cartDialogRef}
      cart={contents}
      codeDiscountPercentage={Number(codeDiscountPercentage) || undefined}
      freeShippingMethod={config?.free_shipping_method?.value}
      freeShippingThreshold={
        parseFloat(config?.free_shipping_threshold?.value, 10) || undefined
      }
      isOpen={isOpen}
      onAddToCart={handleAddToCart}
      onRequestClose={() => setIsOpen(false)}
      onQuantityUpdate={handleQuantityUpdate}
      prioritizedGiftingEnabled={config?.prioritizedGiftingEnabled?.value}
      shippingPolicy={config?.shipping_policy_modal}
      upsellConfig={config?.upsellConfig}
    />
  );
};

const CartDialogWithData = (props) => {
  const { data } = useQuery(SITE_CONFIG, {
    variables: { key: 'cart', preview: isPreview('settings') }
  });
  const cartConfig = data?.settings[0].values;

  if (!cartConfig) return null;
  return <CartDialogContainer config={cartConfig} {...props} />;
};

CartDialogContainer.propTypes = {
  /**
   * Cart configs from contentful
   */
  config: PropTypes.shape({
    /**
     * A new user coupon
     */
    coupon_code: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    /**
     * Until Cart API lets us query for a coupon code's discount percentage:
     * Fetch the discount code percentage set in Contentful; it should match the coupon code's discount percentage set in Shopify
     */
    coupon_code_percentage: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    free_shipping_threshold: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    free_shipping_method: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    /**
     * Site-wide coupon auto apply is turned on
     */
    is_discount_auto_applied: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.bool
    }),
    /**
     * A coupon code for account activation
     */
    loyalty_coupon: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.string
    }),
    /**
     * Controls placement of gifting upsell in cart and cart dialog
     * True = show gifting upsell at top of cart and cart dialog
     */
    prioritizedGiftingEnabled: PropTypes.shape({
      key: PropTypes.string,
      value: PropTypes.bool
    }),
    shipping_policy_modal: PropTypes.shape({
      cta: PropTypes.string,
      display: PropTypes.bool,
      title: PropTypes.string,
      sections: PropTypes.arrayOf(PropTypes.shape({})),
      defaultSection: PropTypes.shape({})
    }),
    upsellConfig: PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.shape({}),
      content: PropTypes.arrayOf(PropTypes.shape({}))
    })
  }).isRequired
};

CartDialogContainer.displayName = 'CartDialogContainer';

export { CartDialogContainer };
export default CartDialogWithData;
